// src/services/permissions.service.ts
import { PermissionString, parsePermission } from '../types/permission.types';

/**
 * Permission service for checking user permissions
 */
export class PermissionsService {
  /**
   * Check if user has a specific permission
   * Admin users (with "*.*.*" permission) have all permissions
   * @param userPermissions - Array of user permission strings
   * @param permission - Permission string to check
   * @returns true if user has the permission
   */
  can(userPermissions: PermissionString[], permission: PermissionString): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }

    // Admin has all permissions
    if (userPermissions.includes('*.*.*')) {
      return true;
    }

    return userPermissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param userPermissions - Array of user permission strings
   * @param permissions - Array of permission strings to check
   * @returns true if user has at least one permission
   */
  hasAnyPermission(userPermissions: PermissionString[], permissions: PermissionString[]): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }
    // Same bypass as can(): backend sends only *.*.* for active admin role
    if (userPermissions.includes('*.*.*')) {
      return true;
    }
    return permissions.some((permission) => userPermissions.includes(permission));
  }

  /**
   * Check if user has all of the specified permissions
   * @param userPermissions - Array of user permission strings
   * @param permissions - Array of permission strings to check
   * @returns true if user has all permissions
   */
  hasAllPermissions(userPermissions: PermissionString[], permissions: PermissionString[]): boolean {
    if (!userPermissions || userPermissions.length === 0) {
      return false;
    }
    if (userPermissions.includes('*.*.*')) {
      return true;
    }
    return permissions.every((permission) => userPermissions.includes(permission));
  }

  /**
   * Get permissions filtered by module
   * @param userPermissions - Array of user permission strings
   * @param module - Module name
   * @returns Array of permission strings for the module
   */
  getPermissionsByModule(userPermissions: PermissionString[], module: string): PermissionString[] {
    if (!userPermissions || userPermissions.length === 0) {
      return [];
    }
    return userPermissions.filter((perm) => {
      try {
        const parsed = parsePermission(perm);
        return parsed.module === module;
      } catch {
        return false;
      }
    });
  }

  /**
   * Get permissions filtered by module and menu
   * @param userPermissions - Array of user permission strings
   * @param module - Module name
   * @param menu - Menu name
   * @returns Array of permission strings for the module and menu
   */
  getPermissionsByModuleAndMenu(
    userPermissions: PermissionString[],
    module: string,
    menu: string,
  ): PermissionString[] {
    if (!userPermissions || userPermissions.length === 0) {
      return [];
    }
    return userPermissions.filter((perm) => {
      try {
        const parsed = parsePermission(perm);
        return parsed.module === module && parsed.menu === menu;
      } catch {
        return false;
      }
    });
  }
}

// Export singleton instance
export const permissionsService = new PermissionsService();
